// @flow

import PMTEventEmitter from './PMTEventEmitter';
import AppDispatcher from './AppDispatcher';
import _ from 'lodash';
import invariant from '../utils/invariant'

/**
 * This class represents the most basic functionality for a FluxStore. Do not
 * extend this store directly; instead extend FluxReduceStore when creating a
 * new store.
 *
 * Inspired by https://github.com/facebook/flux/blob/master/src/stores/FluxStore.js
 * We add some PayMyTable code for legacy and transition.
 */
class Store {

  // private
  _dispatchToken: string;

  // protected, available to subclasses
  __changed: boolean;
  __changeEvent: string;
  __className: any;
  __dispatcher: Dispatcher<any>;
  __emitter: EventEmitter;

  constructor(dispatcher: Dispatcher<any> = null): void {
    this.__className = this.constructor.name;

    this.__changed = false;
    this.__changeEvent = 'change';

    this.__emitter = new PMTEventEmitter();

    if (dispatcher == null) {
      // pmt flux implementation
      this.__dispatcher = AppDispatcher;
      this._dispatchToken = this.__dispatcher.register((payload) => {
        this.__invokeOnDispatch(payload);
      });
    } else {
      // real flux
      this.__dispatcher = dispatcher;
      this._dispatchToken = this.__dispatcher.register((payload) => {
        this.__invokeOnDispatch(payload);
      });
    }
  }

  /**
   * Should be named addListener, but we use our custom, so we rename it to `addListenerFlux`
   */
  addListenerFlux(callback: (eventType?: string) => void): {remove: () => void} {
    return this.__emitter.addListener(this.__changeEvent, callback);
  }

  addListener(event: string, callback: Function) {
    if (_.isFunction(event)) {
      this.addListenerFlux(event);
    } else {
      this.__emitter.addListener(event, callback);
    }
  }

  removeListener(event: string, callback) {
    if (_.isFunction(event)) {
      this.addListenerFlux(event);
    } else {
      this.__emitter.removeListener(event, callback);
    }
  }

  //
  // PayMyTable legacy code
  //


  addListeners(listeners: Array<Listener>) {
    _.each(listeners, (listener: Listener) => {
      this.__emitter.addListener(listener.event, listener.func);
    });
  }

  removeListeners(listeners: Array<Listener>) {
    _.each(listeners, (listener: Listener) => {
      invariant(
        _.isFunction(listener.func),
        `[Listener] REMOVE - Invalid listener for event ${listener.event}`
      );
      this.__emitter.removeListener(listener.event, listener.func);
    });
  }

  emitEvent(event: string) {
    this.__emitter.emit(event);
  }

  //

  getDispatcher(): Dispatcher<any> {
    return this.__dispatcher;
  }

  /**
   * This exposes a unique string to identify each store's registered callback.
   * This is used with the dispatcher's waitFor method to declaratively depend
   * on other stores updating themselves first.
   */
  getDispatchToken(): string {
    return this._dispatchToken;
  }

  waitFor(tokens) {
    this.__dispatcher.waitFor(tokens);
  }

  /**
   * Returns whether the store has changed during the most recent dispatch.
   */
  hasChanged(): boolean {
    invariant(
      this.__dispatcher.isDispatching(),
      '%s.hasChanged(): Must be invoked while dispatching.',
      this.__className
    );
    return this.__changed;
  }

  __emitChange(): void {
    invariant(
      this.__dispatcher.isDispatching(),
      '%s.__emitChange(): Must be invoked while dispatching.',
      this.__className
    );
    this.__changed = true;
  }

  /**
   * This method encapsulates all logic for invoking __onDispatch. It should
   * be used for things like catching changes and emitting them after the
   * subclass has handled a payload.
   */
  __invokeOnDispatch(payload: Object): void {
    this.__changed = false;
    this.__onDispatch(payload);
    if (this.__changed) {
      this.__emitter.emit(this.__changeEvent);
    }
  }

  /**
   * The callback that will be registered with the dispatcher during
   * instantiation. Subclasses must override this method. This callback is the
   * only way the store receives new data.
   */
  __onDispatch(payload: Object): void {
    invariant(
      false,
      '%s has not overridden FluxStore.__onDispatch(), which is required',
      this.__className
    );
  }

}

export default Store;



// WEBPACK FOOTER //
// ./sdk/scripts/flux/Store.js